import { useState } from "react";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const MOCK_PLAYERS = [
  { name: "Quinn F",     academy: "Northside Cricket", ageGroup: "U13", dob: "2013-03-15", status: "verified", flagReason: null },
  { name: "Ansh S",     academy: "Westfield Cricket",  ageGroup: "U13", dob: "2013-07-20", status: "verified", flagReason: null },
  { name: "Nirvaan P",  academy: "Northside Cricket",  ageGroup: "U13", dob: "2012-11-02", status: "flagged",  flagReason: "DOB places player in U15 bracket" },
  { name: "Jordan L",   academy: "Eastside Academy",   ageGroup: "U13", dob: "2013-05-10", status: "verified", flagReason: null },
  { name: "Daron W",    academy: "Eastside Academy",   ageGroup: "U13", dob: "2013-09-18", status: "pending",  flagReason: null },
  { name: "Marcus J",   academy: "Central Sports",     ageGroup: "U13", dob: "2013-01-22", status: "verified", flagReason: null },
  { name: "Aryan S",    academy: "Northside Cricket",  ageGroup: "U11", dob: "2015-04-11", status: "verified", flagReason: null },
  { name: "Dev P",      academy: "Northside Cricket",  ageGroup: "U11", dob: "2015-08-30", status: "verified", flagReason: null },
  { name: "Rohan T",    academy: "Westfield Cricket",  ageGroup: "U11", dob: "2014-12-05", status: "flagged",  flagReason: "DOB places player in U13 bracket" },
  { name: "Kiran B",    academy: "Central Sports",     ageGroup: "U11", dob: "2015-02-14", status: "pending",  flagReason: null },
  { name: "Shane M",    academy: "Eastside Academy",   ageGroup: "U15", dob: "2011-06-08", status: "verified", flagReason: null },
  { name: "Brad T",     academy: "Eastside Academy",   ageGroup: "U15", dob: "2011-09-15", status: "verified", flagReason: null },
];

const AGE_GROUPS = ["All", "U11", "U13", "U15", "U17"] as const;
type AgeFilter = (typeof AGE_GROUPS)[number];

const STATUS_STYLE: Record<string, { badge: string; row: string }> = {
  verified: { badge: "bg-green-100 text-green-700 border-green-300",  row: "" },
  pending:  { badge: "bg-amber-100 text-amber-700 border-amber-300",  row: "bg-amber-50" },
  flagged:  { badge: "bg-red-100   text-red-700   border-red-300",    row: "bg-red-50" },
};

export default function HubAgeVerificationDemo() {
  const [ageFilter, setAgeFilter] = useState<AgeFilter>("All");
  const [alertShown, setAlertShown] = useState(false);

  const filtered = ageFilter === "All"
    ? MOCK_PLAYERS
    : MOCK_PLAYERS.filter((p) => p.ageGroup === ageFilter);

  const verifiedCount = MOCK_PLAYERS.filter((p) => p.status === "verified").length;
  const pendingCount  = MOCK_PLAYERS.filter((p) => p.status === "pending").length;
  const flaggedCount  = MOCK_PLAYERS.filter((p) => p.status === "flagged").length;

  function handleEligibilityCheck() {
    setAlertShown(true);
    setTimeout(() => setAlertShown(false), 5000);
  }

  return (
    <HubAdminDemoLayout>
      <div className="px-4 md:px-8 py-6 space-y-5" data-testid="hub-age-verification-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">Age Verification</h1>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
          </div>
          <Button
            size="sm"
            className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
            onClick={handleEligibilityCheck}
            data-testid="btn-run-eligibility"
          >
            Run Eligibility Check
          </Button>
        </div>

        {/* AI alert */}
        {alertShown && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="eligibility-alert">
            <span className="font-semibold">AI Eligibility Filter Agent</span> validates DOB against age-group gates and cross-checks yo-yo benchmarks. Available in Phase 2.
          </div>
        )}

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3" data-testid="summary-bar">
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-center">
            <p className="text-2xl font-bold text-green-700" data-testid="count-verified">{verifiedCount}</p>
            <p className="text-xs text-green-600 mt-0.5">Verified</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
            <p className="text-2xl font-bold text-amber-700" data-testid="count-pending">{pendingCount}</p>
            <p className="text-xs text-amber-600 mt-0.5">Pending</p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center">
            <p className="text-2xl font-bold text-red-700" data-testid="count-flagged">{flaggedCount}</p>
            <p className="text-xs text-red-600 mt-0.5">Flagged</p>
          </div>
        </div>

        {/* Age filter */}
        <div className="flex flex-wrap gap-2" data-testid="age-filter-pills">
          {AGE_GROUPS.map((ag) => (
            <button
              key={ag}
              onClick={() => setAgeFilter(ag)}
              data-testid={`age-pill-${ag.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                ageFilter === ag
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
              }`}
            >
              {ag}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="player-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Academy</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Age Group</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">DOB</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Flag Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((player) => {
                  const style = STATUS_STYLE[player.status];
                  return (
                    <tr
                      key={`${player.name}-${player.ageGroup}`}
                      className={style.row}
                      data-testid={`player-row-${player.status === "flagged" ? "flagged" : player.status}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{player.name}</td>
                      <td className="px-4 py-3 text-gray-600">{player.academy}</td>
                      <td className="px-4 py-3 text-gray-600">{player.ageGroup}</td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">{player.dob}</td>
                      <td className="px-4 py-3">
                        <Badge className={`border text-xs ${style.badge}`}>{player.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {player.flagReason ? (
                          <span className="text-red-600 flex items-center gap-1">
                            <span>⚠️</span>
                            <span className="text-xs">{player.flagReason}</span>
                          </span>
                        ) : player.status === "verified" ? (
                          <span className="text-green-600 font-medium">✓</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info box */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="info-box">
          Age eligibility is a mandatory gate for hub selection. Flagged players must resolve their DOB
          with their academy admin before Phase 2 scoring begins.
        </div>
      </div>
    </HubAdminDemoLayout>
  );
}
